import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// VITE_API_URL includes /api/v1 (e.g. http://localhost:4000/api/v1)
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';

type State = 'idle' | 'submitting' | 'success' | 'duplicate' | 'error';

interface WaitlistModalProps {
  isOpen: boolean;
  source: string;
  onClose: () => void;
}

export function WaitlistModal({ isOpen, source, onClose }: WaitlistModalProps) {
  const [state, setState] = useState<State>('idle');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setState('idle');
      setEmail('');
      setName('');
      setTimeout(() => firstInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setState('submitting');

    try {
      const res = await fetch(`${API_URL}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name || undefined, source }),
      });

      const json = await res.json();

      if (!res.ok) {
        setState('error');
        return;
      }

      if (json.data?.alreadyRegistered) {
        setState('duplicate');
      } else {
        setState('success');
      }
    } catch {
      setState('error');
    }
  }

  if (!isOpen) return null;

  const modal = (
    <div
      className="waitlist-modal-backdrop"
      role="presentation"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="waitlist-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="waitlist-modal-title"
      >
        <button type="button" className="waitlist-modal-close" aria-label="Close" onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </button>

        {state === 'success' ? (
          <div className="waitlist-modal-success">
            <div className="waitlist-modal-success-icon" aria-hidden="true">
              &#10003;
            </div>
            <h2>You&apos;re on the list!</h2>
            <p>We&apos;ll be in touch when Filmspo opens its doors. Keep an eye on your inbox.</p>
            <button type="button" className="button button-gold" onClick={onClose}>
              Done
            </button>
          </div>
        ) : state === 'duplicate' ? (
          <div className="waitlist-modal-success">
            <div className="waitlist-modal-success-icon" aria-hidden="true">
              &#10003;
            </div>
            <h2>You&apos;re already on the list!</h2>
            <p>Looks like you signed up before &mdash; we&apos;ve got your spot saved.</p>
            <button type="button" className="button button-gold" onClick={onClose}>
              Got it
            </button>
          </div>
        ) : (
          <>
            <h2 id="waitlist-modal-title">Join the Waitlist</h2>
            <p className="waitlist-modal-subtitle">
              Be among the first to access Filmspo when we launch.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div className="waitlist-modal-field">
                <label htmlFor="waitlist-name">
                  Name <span className="waitlist-modal-optional">(optional)</span>
                </label>
                <input
                  ref={firstInputRef}
                  id="waitlist-name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name"
                  maxLength={100}
                  autoComplete="name"
                />
              </div>

              <div className="waitlist-modal-field">
                <label htmlFor="waitlist-email">
                  Email <span aria-hidden="true">*</span>
                </label>
                <input
                  id="waitlist-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              {state === 'error' && (
                <p className="waitlist-modal-error" role="alert">
                  Something went wrong. Please try again.
                </p>
              )}

              <button
                type="submit"
                className="button button-gold waitlist-modal-submit"
                disabled={state === 'submitting' || !email}
              >
                {state === 'submitting' ? 'Joining\u2026' : 'Join the Waitlist'}
              </button>
            </form>

            <p className="waitlist-modal-disclaimer">
              No spam. We&apos;ll only email you when access is ready.
            </p>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
